export type AvailableCountriesT = 'DK' | 'SE' | 'NO' | 'FI' | 'FO'

export type LocalId = {
  id: string
  country: AvailableCountriesT
}

export type MinimalisticUser = {
  email: string
  name: string
  // TODO @Shtono currently this field is mandatory
  phone_number: string
}

export type CreateCustomerDataT = {
  local_organization_id: LocalId
  name: string
  responsible_user_id: number
  subscription_plan: SubscriptionPlan
  userData: MinimalisticUser[] | []
}

export type Address = {
  city?: string
  coname?: string
  coordinates?: {
    lat: number
    lng: number
  }
  country?: string
  local_region?: string
  municipality?: string
  number?: string
  postdistrict?: string
  street?: string
  zipcode?: number
}

export type AvailableCountriesLower = 'dk' | 'se' | 'no' | 'fi' | 'fo' // This should be deprecated at some point
export type AvailableCountriesCapital = 'DK' | 'SE' | 'NO' | 'FI' | 'FO'
export const availableCountriesArr = ['DK', 'SE', 'NO', 'FI', 'FO']
export type AvailableCountries =
  | AvailableCountriesLower
  | AvailableCountriesCapital

export type CustomerT = {
  id: number
  name: string
  country: AvailableCountriesCapital
  local_id: string
  is_revoked: boolean
  responsible_user_id: number
  domain?: string
  is_removed: boolean
  expires_at?: string
  updated_at?: string
  created_at?: string
  accounts: number
  last_login?: string
  responsible_user: string
}

export type SuccessResponseT<T> = {
  success: true
  data: T
}

export type ErrorResponseT = {
  success: false
  data: null
  error: {
    name: string
    message: string
  }
}

export type WebApi2Response<T> = SuccessResponseT<T> | ErrorResponseT

export type ScoreModel = {
  model: 'BOHR' | 'NEWTON'
  explanation?: boolean
}

export type AllowModels = {
  country: 'DK' | 'SE' | 'NO' | 'FI' | 'FO'
  score_models: ScoreModel[]
}

export type SubscriptionPlan =
  | 'TRIAL_V2'
  // 14-day Risika for AI trial, 200 unique Nordic lookups (ENG2-2614; 200 since ENG2-2623).
  | 'TRIAL_MCP'
  | 'MANUAL_PACKAGE'
  | 'STRATEGIC_PACKAGE'

export type LegacySubscriptionPlan =
  | 'FREEMIUM'
  | 'TRIAL'
  | 'BASIC'
  | 'STANDARD'
  | 'PRO'
  | 'ENTERPRISE'

export type KnownSubscriptionPlan = SubscriptionPlan | LegacySubscriptionPlan

export const ACTIVE_SUBSCRIPTION_PLANS: readonly SubscriptionPlan[] = [
  'TRIAL_V2',
  'TRIAL_MCP',
  'MANUAL_PACKAGE',
  'STRATEGIC_PACKAGE',
]

export type RisikaUserT = {
  id: number
  email: string
  name?: string
}

export type UserRoleT = 'user' | 'admin'

export type ApiUserT = {
  id: number
  nickname: string
  company_id: number
  grand_permit: null | unknown[]
  created_by: number
  exp: string
  revoked: boolean
}

export type UserT = {
  id: number
  role: UserRoleT
  email: string
  password: string
  refresh_token?: string
  name?: string
  phone_number?: string
  api_user_id: number
  is_verified: boolean
  should_change_password: boolean
  last_login?: string
  updated_at?: string
  created_at?: string
  customer_id: number
}

export type UserBeforeT = {
  userName: string
  email: string
}

export type UserAfterT = {
  name: string
  email: string
  customer_id: number
}

export type CreateUserT = {
  name: string
  email: string
  customer_id: number
  phone?: string
  role?: UserRoleT
}

export type UserTableT = {
  userId: number
  lastLogin?: string
  name?: string
  email: string
  phoneNumber?: string
  isVerified: boolean
  role: UserRoleT
}

export type ItemT = {
  title: string
  field: string
  isDate?: boolean
}

export type UserSettingsTableNameEnum =
  | 'credit_check'
  | 'company_info'
  | 'monitor_table'
  | 'credit_check_sidebar'
  | 'fraud_indicators'

export type Zone = {
  zone_id: number
  name: string
  countries: string[]
  created_at: string
}

export type EscalationStatusValue = 'OPEN' | 'REASON_RECORDED' | 'CLOSED'

/** A customer that raised an escalation. `name` is null when the company id no
 *  longer resolves (a revoked customer); the id is still shown. */
export type EscalationCustomerRef = {
  company_id: number
  name: string | null
}

/** A dashboard user that raised an escalation. Empty for rows created before
 *  RisikaAPI started recording the escalating user — those are not
 *  backfillable, and render as a dash. */
export type EscalationUserRef = {
  api_user_id: number
  name: string | null
  email: string | null
}

export type EscalationListItem = {
  id: string
  country: string
  local_id: string
  /** null when the company is absent from the Coface company cache */
  company_name: string | null
  status: EscalationStatusValue
  reason: string | null
  reason_recorded_by: string | null
  escalation_count: number
  escalated_at: string
  last_escalated_at: string
  updated_at: string
  escalated_by: EscalationCustomerRef[]
  escalated_by_users: EscalationUserRef[]
}

export type EscalationListPagination = {
  page: number
  page_size: number
  total: number
  pages: number
}

export type EscalationListResponse = {
  escalations: EscalationListItem[]
  /** Every country that has at least one escalation, ignoring the active
   *  filters — this is what the country dropdown is built from. */
  available_countries: string[]
  pagination: EscalationListPagination
}

export type EscalationListParams = {
  page?: number
  pageSize?: number
  countries?: string[]
  status?: EscalationStatusValue[]
  localId?: string
  q?: string
}

export type RecordEscalationReasonPayload = {
  local_id: string
  reason: string
  reason_recorded_by: string
}

/**
 * Mirrors `CustomerGroupRow` in the CMAPI's
 * `database/types/CustomerGroup.types.ts`. Dates arrive as ISO
 * strings over the wire — they're typed as `string` here rather
 * than `Date` because the JSON envelope doesn't hydrate them.
 */
export type CustomerGroupT = {
  id: string
  name: string
  description: string | null
  customer_ids: number[]
  created_by: string
  created_at: string
  updated_at: string
}

export type CreateCustomerGroupDataT = {
  name: string
  description?: string | null
  customer_ids: number[]
}

export type UpdateCustomerGroupDataT = Partial<CreateCustomerGroupDataT>

export type DeleteCustomerGroupSuccessT = {
  id: string
}

/**
 * 409 body returned by `DELETE /customer-group/:id` when the group is
 * still referenced by at least one non-deleted banner. The CMD UI
 * shows the operator the banner IDs that need to be unhooked first.
 */
export type CustomerGroupInUseErrorT = {
  success: false
  error: {
    name: 'CustomerGroupInUse'
    message: string
    referencing_banner_ids: string[]
  }
}

/**
 * Lightweight typeahead row returned by `GET /customer?q=...`. Used
 * by the customer-group member picker to render `<local_id> — <name>`
 * chips without a second fetch.
 */
export type CustomerTypeaheadResultT = {
  id: number
  local_id: string
  name: string
  country: string
}

/**
 * Banner severity. Drives the chip colour in the list and disables
 * the dismissibility toggle in the dialog when set to `error` — the
 * parent ticket (ENG2-1996) mandates that error banners stay pinned
 * until an operator deletes or ends them.
 */
export type BannerSeverityT = 'info' | 'warning' | 'error' | 'success'

/**
 * Roles the banner can target. Matches the CMAPI's role enum —
 * keep in sync with `database/types/Banner.types.ts` over there.
 */
export type BannerRoleT = 'admin' | 'user'

/**
 * Mirrors the CMAPI row shape. Dates ship over the wire as ISO
 * strings, so they're typed as `string` rather than `Date` (the
 * envelope does not hydrate them).
 */
export type BannerT = {
  id: string
  severity: BannerSeverityT
  // All four locale message bodies are required — every banner ships
  // with DA / EN / NB / SV translations and there is no dashboard-side
  // fallback to English.
  message_da: string
  message_en: string
  message_nb: string
  message_sv: string
  cta_label_da: string | null
  cta_label_en: string | null
  cta_label_nb: string | null
  cta_label_sv: string | null
  // Per-language CTA URLs (ENG2-2106) — the only CTA URL shape. CMAPI
  // enforces the set is all-or-nothing; "one URL for everyone" is the
  // same value in all four columns.
  cta_url_da: string | null
  cta_url_en: string | null
  cta_url_nb: string | null
  cta_url_sv: string | null
  starts_at: string
  ends_at: string | null
  dismissible: boolean
  target_all: boolean
  target_group_ids: string[]
  target_customer_ids: number[]
  target_roles: BannerRoleT[]
  created_by: string
  created_at: string
  updated_at: string
  deleted_at: string | null
}

/**
 * Audience selection — the load-bearing chunk of the banner form.
 * Lives outside `CreateBannerDataT` so the `<AudienceSelector />`
 * component can be reused (and unit-tested) in isolation.
 */
export type BannerAudienceT = {
  target_all: boolean
  target_group_ids: string[]
  target_customer_ids: number[]
  target_roles: BannerRoleT[]
}

export type CreateBannerDataT = {
  severity: BannerSeverityT
  // All four locale message bodies are required on create. The CMAPI
  // validator enforces minLength 1; empty string and null both reject.
  message_da: string
  message_en: string
  message_nb: string
  message_sv: string
  cta_label_da?: string | null
  cta_label_en?: string | null
  cta_label_nb?: string | null
  cta_label_sv?: string | null
  cta_url_da?: string | null
  cta_url_en?: string | null
  cta_url_nb?: string | null
  cta_url_sv?: string | null
  starts_at: string
  ends_at?: string | null
  dismissible: boolean
  target_all: boolean
  target_group_ids: string[]
  target_customer_ids: number[]
  target_roles: BannerRoleT[]
}

export type UpdateBannerDataT = Partial<CreateBannerDataT>

export type DeleteBannerSuccessT = {
  id: string
}
